/**
 *  @class
 *  @function DeliveryCheck
 *  Estimates delivery dates and cash on delivery for an Indian PIN code from zone rules set in
 *  the product page's Delivery check block. Rules are read from a JSON script inside the element.
 *  The prompt is a <details> that opens the PIN field; once a PIN is checked the field closes, the
 *  prompt becomes "Delivering to <PIN> Change" and the estimate shows below it. The last PIN code
 *  checked is remembered in the browser so returning shoppers see their estimate straight away.
 */
if (!customElements.get('delivery-check')) {
  class DeliveryCheck extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.form = this.querySelector('form');
      this.input = this.querySelector('input');
      this.prompt = this.querySelector('.delivery-check--prompt');
      this.change = this.querySelector('.delivery-check--change');
      this.pinLabel = this.querySelector('.delivery-check--pin');
      this.status = this.querySelector('.delivery-check--status');
      this.resultRow = this.querySelector('.delivery-check--result-row');
      this.result = this.querySelector('.delivery-check--result');
      try {
        this.config = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (error) {
        return;
      }
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.check(this.input.value.trim());
      });
      this.details.addEventListener('toggle', () => {
        // Selected, so a new PIN typed over the saved one replaces it instead of being cut off at six digits.
        if (!this.details.open) return;
        this.input.focus();
        this.input.select();
      });

      let saved = null;
      try {
        saved = window.localStorage.getItem('delivery-check-pin');
      } catch (error) {}
      if (saved) {
        this.input.value = saved;
        this.check(saved, true);
      }
      // The result's live region: always in the page (the row inside it is what shows and hides), and made live
      // only after the saved PIN's estimate is in, so a returning shopper hears answers to their own checks only.
      this.status.setAttribute('role', 'status');
    }
    zoneFor(pin) {
      for (const zone of this.config.zones) {
        if (zone.prefixes.some((prefix) => prefix && pin.startsWith(prefix))) return zone;
      }
      return this.config.fallback;
    }
    formatDate(date) {
      const options = { weekday: 'short', day: 'numeric', month: 'short' };
      try {
        return date.toLocaleDateString(this.config.locale || 'en-IN', options);
      } catch {
        return date.toLocaleDateString('en-IN', options);
      }
    }
    check(pin, quiet) {
      if (!/^[1-9][0-9]{5}$/.test(pin)) {
        if (!quiet) {
          this.show(this.config.strings.invalid, true);
          this.input.setAttribute('aria-invalid', 'true');
        }
        return;
      }
      this.input.removeAttribute('aria-invalid');
      try {
        window.localStorage.setItem('delivery-check-pin', pin);
      } catch (error) {}

      const zone = this.zoneFor(pin);
      const buffer = this.config.buffer;
      const from = new Date();
      const to = new Date();
      from.setDate(from.getDate() + buffer + zone.min);
      to.setDate(to.getDate() + buffer + zone.max);

      const noCod = this.config.noCod.some((prefix) => prefix && pin.startsWith(prefix));
      const lines = [
        this.config.strings.delivery.replace('[from]', this.formatDate(from)).replace('[to]', this.formatDate(to)),
        noCod ? this.config.strings.codNo : this.config.strings.codYes
      ];
      this.pinLabel.textContent = this.config.strings.deliveringTo.replace('[pin]', pin);
      this.prompt.hidden = true;
      this.change.hidden = false;
      // Closing the field hides the focused input, so focus moves to "Delivering to <PIN> Change".
      const hadFocus = this.details.contains(document.activeElement) && document.activeElement !== this.details.querySelector('summary');
      this.details.open = false;
      if (hadFocus) this.details.querySelector('summary').focus();
      this.show(lines.filter(Boolean).join('<br>'), false);
    }
    show(html, isError) {
      this.result.innerHTML = html;
      this.resultRow.hidden = false;
      this.resultRow.classList.toggle('delivery-check--error', isError);
    }
  }
  customElements.define('delivery-check', DeliveryCheck);
}
